import { useOutsideClick } from '@quadminds/hooks';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { Calendar } from 'react-date-range';
import { twMerge } from 'tailwind-merge';
import { TextField } from '../text-field/textField';

export interface DatePickerI {
    open: boolean;
    onClick: () => void;
    id: string;
    name: string;
    value: string;
    onChange: (item: Date) => void;
    date?: Date;
    label?: string;
    classesInput?: string;
    classesContainer?: string;
    inputClassName?: string;
    error?: boolean;
    minDate?: string;
    maxDate?: string;
    calendarPlacement?: 'top' | 'bottom';
    className?: string;
    escClose?: boolean;
    required?: boolean;
}

export const DatePicker: FC<DatePickerI> = ({
    open,
    onClick,
    id,
    name,
    value,
    onChange,
    date,
    label,
    classesInput,
    classesContainer,
    inputClassName,
    error,
    minDate,
    maxDate,
    className,
    calendarPlacement = 'bottom',
    escClose = true,
    required = true,
}) => {
    const minDateFromString = minDate ? moment(minDate, 'DD/MM/YYYY').toDate() : undefined;
    const maxDateFromString = maxDate ? moment(maxDate, 'DD/MM/YYYY').toDate() : undefined;

    const calendarClass = `shadow-md  ${
        calendarPlacement === 'top'
            ? 'absolute bottom-full mb-1 w-[400px] z-50 font-poppins capitalize'
            : 'absolute w-[400px] z-50 font-poppins capitalize'
    }`;

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (!open) return;
            if (event.key === 'Escape' && escClose) {
                onClick();
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, [escClose, onClick, open]);

    const ref = useOutsideClick(() => {
        if (open) {
            onClick();
        }
    });

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            className={twMerge('h-full relative w-[500px]', classesContainer, className)}
        >
            <TextField
                id={id}
                name={name}
                rounded="standard"
                label={label}
                value={value}
                classNameContainer={classesInput}
                className={inputClassName}
                endButton={{
                    iconName: 'ri-calendar-event-line',
                    background: 'transparent',
                }}
                onClick={onClick}
                required={required}
                error={error}
            />
            {open && (
                <Calendar
                    onChange={onChange}
                    locale={es}
                    date={date}
                    className={calendarClass}
                    minDate={minDateFromString}
                    maxDate={maxDateFromString}
                    data-test-id="calendar"
                />
            )}
        </div>
    );
};

export default DatePicker;
